import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import Dropdown from 'rc-dropdown';
import Menu, { Item as MenuItem } from 'rc-menu';
import 'rc-dropdown/assets/index.css';
import PropTypes from 'prop-types';
import styles from './AddTopLevelIconAndText.module.css';
import { getAvailableExports, executeExport, ExportTypes } from '../../Managers/ExportManager';
import { CAMPAIGN_STATUS, CAMPAING_PROMISE_TYPES } from '../../../../constants/enums';
import campaignValues from '../../../../utils/campaignValues';

const IMAGE_SOURCES = {
    COMPLETED: require('../../../../images/Icon_completed.png'),
    STARTS_IN: require('../../../../images/Icon_starts_in.png'),
    ONGOING: require('../../../../images/Icon_ongoing.png'),
    DOWNLOAD: require('../../../../images/icon_download_no_background.svg'),
    ARROW_DOWN: require('../../../../images/icon_arrow_down.svg') 
};

// Modal component for displaying saved map URL
const SavedMapModal = ({ isOpen, mapUrl, onClose }) => {
    React.useEffect(() => {
        // Add/remove modalOpen class to body when modal opens/closes
        if (isOpen) {
            document.body.classList.add('modalOpen');

            // Add event listener to prevent clicks on submenu
            const interceptClickEvents = (e) => {
                const submenu = document.querySelector('[class*="submenu"]');
                if (submenu && submenu.contains(e.target)) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            };

            document.addEventListener('click', interceptClickEvents, true);

            return () => {
                document.body.classList.remove('modalOpen');
                document.removeEventListener('click', interceptClickEvents, true);
            };
        }

        return () => {
            document.body.classList.remove('modalOpen');
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleCopyToClipboard = () => {
        navigator.clipboard.writeText(mapUrl).then(() => {
            alert('URL copied to clipboard!');
        }).catch(err => {
            console.error('Could not copy URL: ', err);
        });
    };

    // Use createPortal to render the modal at the document body level to make sure that everything is disabled
    return ReactDOM.createPortal(
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <h3>Map Saved Successfully</h3>
                <p>Your map has been saved and can be accessed at:</p>
                <div className={styles.urlContainer}>
                    <input
                        type="text"
                        value={mapUrl}
                        readOnly
                        className={styles.urlInput}
                    />
                    <button
                        className={styles.copyButton}
                        onClick={handleCopyToClipboard}
                    >
                        Copy
                    </button>
                </div>
                <p className={styles.modalNote}>
                    Share this URL to allow others to view the exact same map.
                </p>
                <div className={styles.modalButtons}>
                    <button className={styles.closeButton} onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

const AddTopLevelIconAndText = (WrappedComponent) => {
    return function WithIconComponent({
        showImportCampaignDateText = true,
        showClientOrderNumberText = true,
        hocCampaignStatus = CAMPAIGN_STATUS.NOT_STARTED,
        clientOrderNumber = "",
        importCampaignDate = "",
        productPromiseDescription = "",
        campaignStartsInDays = "",
        disabledExports = [],
        showButtonBorder = false,
        ...props
    }) {
        const [isExporting, setIsExporting] = useState(false);
        const [exportError, setExportError] = useState(null);
        const [dropdownVisible, setDropdownVisible] = useState(false);
        const [isMapModalOpen, setIsMapModalOpen] = useState(false);
        const [savedMapUrl, setSavedMapUrl] = useState('');
        const [hasNoPromise, setHasNoPromise] = useState('');
        const [isNonMOMSFollowUpCampaign, setIsNonMOMSFollowUpCampaign] = useState(false);

        useEffect(() => {
            setHasNoPromise(props.campaignPromiseType === CAMPAING_PROMISE_TYPES.SE_DIGITAL_NO_PROMISE);
            setIsNonMOMSFollowUpCampaign(
                props.campaignPromiseType ? campaignValues.isNonMOMSFollowUpCampaign(props.campaignPromiseType) : false
            );
        }, [props.campaignPromiseType]);

        useEffect(() => {
            if (props.onSaveMap) {
                props.onSaveMap(setMapInstance);
            }
        }, [props.onSaveMap]);

        const exportProps = {
            ...props
        };

        const availableExports = getAvailableExports(exportProps, disabledExports || []);
        const handleExport = useCallback((exportKey) => {
            // If already exporting, prevent duplicate operations
            if (isExporting) return;

            setIsExporting(true);
            setDropdownVisible(false);
            setExportError(null);

            const safeExportProps = {
                ...exportProps,
                campaignType: exportProps.campaignType || null,
                orderData: exportProps.orderData || null,
                panels: exportProps.panels || [],
                onExportAddressList: exportProps.onExportAddressList
                    ? (...args) => {
                        try {
                            const result = exportProps.onExportAddressList(...args);

                            // If it's a promise, wrap it to catch errors
                            if (result && typeof result.then === 'function') {
                                return result.catch(err => {
                                    console.error('Error in onExportAddressList promise:', err);
                                    throw new Error(`Address list export failed: ${err.message || 'Unknown error'}`);
                                });
                            }

                            return result;
                        } catch (err) {
                            console.error('Error in onExportAddressList:', err);
                            throw new Error(`Address list export failed: ${err.message || 'Unknown error'}`);
                        }
                    }
                    : null
            };

            executeExport(exportKey, safeExportProps)
                .then(result => {
                    //console.log(`Export ${exportKey} completed successfully`);

                    // If this is a map URL export, show the modal with the URL
                    if (exportKey === ExportTypes.MAP_URL && result) {
                        setSavedMapUrl(result);
                        setIsMapModalOpen(true);
                    }
                })
                .catch(error => {
                    console.error(`Export ${exportKey} failed:`, error);
                    setExportError(error.message || 'Export failed');
                })
                .finally(() => {
                    setIsExporting(false);
                    setDropdownVisible(false);
                });
        }, [isExporting, exportProps]);

        const menu = (
            <Menu className={styles.downloadMenu}>
                {availableExports.length > 0 ? (
                    availableExports.map(({ key, label }) => (
                        <MenuItem
                            key={key}
                            className={styles.downloadMenuItem}
                            onClick={() => handleExport(key)}
                        >
                            {label}
                        </MenuItem>
                    ))
                ) : (
                    <MenuItem
                        disabled
                        className={`${styles.downloadMenuItem} ${styles.downloadMenuItemDisabled}`}
                    >
                        No export options available
                    </MenuItem>
                )}
            </Menu>
        );

        const onVisibleChange = (visible) => {
            if (!isExporting) {
                setDropdownVisible(visible);
                if (visible) {
                    setExportError(null);
                }
            }
        };

        const getProductPromiseDescription = (productPromiseDescr) => {
            return (
                <div className={styles.circleContainer}>
                    <div className={`${styles.circleWithText} ${styles.productPromiseColor}`}>
                        <div className={styles.circleText}>{productPromiseDescr}</div>
                    </div>
                    <div className={styles.circleDescription}>Product Promise</div>
                </div>
            );
        };

        const getStatusIconClass = (status) => {
            if (hocCampaignStatus === CAMPAIGN_STATUS.COMPLETED)
                return (
                    <div className={styles.circleContainer}>
                        <div className={styles.circleWithText}>
                            <img
                                src={IMAGE_SOURCES.COMPLETED}
                                alt="Completed"
                                className={styles.circleImage}
                            />
                        </div>
                        <div className={styles.circleDescription}>Completed</div>
                    </div>
                );
            else if (hocCampaignStatus === CAMPAIGN_STATUS.ONGOING)
                return (
                    <div className={styles.circleContainer}>
                        <div className={styles.circleWithText}>
                            <img
                                src={IMAGE_SOURCES.ONGOING}
                                alt="Ongoing"
                                className={styles.circleImage}
                            />
                        </div>
                        <div className={styles.circleDescription}>Ongoing</div>
                    </div>
                );
            else
                return (
                    <div className={styles.circleContainer}>
                        <div className={styles.circleWithText}>
                            <img
                                src={IMAGE_SOURCES.STARTS_IN}
                                alt="StartsIn"
                                className={styles.circleImage}
                            />
                        </div>
                        <div className={styles.circleDescription}>Starts in {campaignStartsInDays} days</div>
                    </div>
                );
        };

        return (
            <div className={`${styles.container}`}>
                <div className={styles.topBar}>
                    <div className={styles.leftSection}>
                        <div className={styles.dropdownContainer}>
                            {/* Always show the dropdown button, but disable if no exports available */}
                            <Dropdown
                                trigger={['click']}
                                overlay={menu}
                                animation="slide-up"
                                onVisibleChange={onVisibleChange}
                                visible={dropdownVisible && availableExports.length > 0 && !isMapModalOpen}
                            >
                                <div onClick={(e) => e.stopPropagation()}>
                                    <button
                                        className={`${styles.downloadButton} 
                                                   ${dropdownVisible ? styles.dropdownOpen : ''}
                                                   ${isExporting || availableExports.length === 0 ? styles.downloadButtonDisabled : ''} 
                                                   ${showButtonBorder ? styles.downloadButtonWithBorder : ''}`}
                                        disabled={isExporting || availableExports.length === 0 || isMapModalOpen}
                                        title={availableExports.length === 0 ? "No export options available" : "Click to see export options"}
                                    >
                                        <img
                                            className={styles.downloadIcon}
                                            src={IMAGE_SOURCES.DOWNLOAD}
                                            alt="Download"
                                        />
                                        {isExporting ? 'Downloading...' : 'Download'}
                                        <img
                                            className={styles.dropdownArrow}
                                            src={IMAGE_SOURCES.ARROW_DOWN}
                                            alt=""
                                        />
                                    </button>
                                </div>
                            </Dropdown>
                            {exportError && (
                                <div className={styles.exportError}>
                                    {exportError}
                                </div>
                            )}
                        </div>
                        <div className={styles.statusTextContainer}>
                            {showImportCampaignDateText && (
                                <div className={styles.statusText}>
                                    Including data until: {importCampaignDate ? importCampaignDate : 'Waiting on data'}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={styles.promiseAndStatusContainer}>
                        {(!hasNoPromise && !isNonMOMSFollowUpCampaign) && getProductPromiseDescription(productPromiseDescription)}
                        <div className={styles.spacer} />
                        {getStatusIconClass(hocCampaignStatus)}
                    </div>
                </div>
                <div className={styles.wrappedContent}>
                    <WrappedComponent {...props} />
                </div>
                <div className={styles.bottomBar}>
                    {showClientOrderNumberText && (
                        <div className={styles.statusText}>
                            PO number: {clientOrderNumber}
                        </div>
                    )}
                </div>

                {/* Map URL Modal */}
                <SavedMapModal
                    isOpen={isMapModalOpen}
                    mapUrl={savedMapUrl}
                    onClose={() => setIsMapModalOpen(false)}
                />
            </div>
        );
    };
};

AddTopLevelIconAndText.propTypes = {
    // Basic HOC props
    showImportCampaignDateText: PropTypes.bool,
    showClientOrderNumberText: PropTypes.bool,
    hocCampaignStatus: PropTypes.string,
    clientOrderNumber: PropTypes.string,
    importCampaignDate: PropTypes.string,
    productPromiseDescription: PropTypes.string,
    campaignStartsInDays: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    disabledExports: PropTypes.arrayOf(PropTypes.string), // Array of export types to disable

    // Props for export functionality
    mediaArray: PropTypes.array, // For Creatives export
    panelPictures: PropTypes.array, // For Pictures export
    campaignExperienceMedia: PropTypes.array, // For Campaign Experience export
    campaignName: PropTypes.string, // Used by various exports
    panels: PropTypes.array, // For Address List export
    allowAddressExport: PropTypes.bool, // For Address List export
    exportPanelSOVValue: PropTypes.bool, // For Address List export
    selectedOrderlineMetaData: PropTypes.array, // For various exports
    orderData: PropTypes.object, // For various exports
    customerId: PropTypes.string, // For various exports
    campaignType: PropTypes.string, // For various exports
    parameters: PropTypes.object, // For various exports

    // Callback functions
    onSaveMap: PropTypes.func, // For URL to Map functionality
    onExportCampaignEffect: PropTypes.func, // For Campaign Effect Measure export
};

export default AddTopLevelIconAndText;